import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ShieldCheck, Smartphone, Building2, FileText, ChevronLeft, CheckCircle2, Copy, Wallet, AlertTriangle } from 'lucide-react';
import { useWallet } from '@/hooks/useWallet';
import { toast } from 'sonner';

type PaymentMethod = 'wallet' | 'chapa' | 'telebirr' | 'bank_transfer' | 'manual';

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string; desc: string; icon: any }[] = [
  { value: 'wallet', label: 'Pay with Wallet', desc: 'Instant — deduct ETB from your wallet balance', icon: Smartphone },
  { value: 'chapa', label: 'Chapa', desc: 'Cards, mobile money, bank — instant', icon: Smartphone },
  { value: 'telebirr', label: 'Telebirr', desc: 'Pay from your Telebirr wallet', icon: Smartphone },
  { value: 'bank_transfer', label: 'Bank Transfer', desc: 'CBE / Awash / Dashen — verified in 1 business day', icon: Building2 },
  { value: 'manual', label: 'Talk to Sales', desc: 'Get a custom invoice / offline payment', icon: FileText },
];

// Real bank account numbers are sent after the inquiry is submitted. Until
// finance provides verified accounts, do not render placeholder numbers in
// the UI — an empty list triggers the "contact us" fallback below.
const BANK_ACCOUNTS: { bank: string; account: string; name: string }[] = [];

interface Pkg {
  id: string;
  name: string;
  tier_label: string | null;
  audience: string;
  price_etb: number;
  price_currency: string;
  duration_days?: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: Pkg | null;
}

export function PackagePurchaseDialog({ open, onOpenChange, pkg }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('chapa');
  const [bankRef, setBankRef] = useState('');
  const [bankName, setBankName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && user) {
      setFullName((user.user_metadata?.full_name as string) || '');
      setEmail(user.email || '');
      setPhone((user.user_metadata?.phone as string) || '');
      setStep(1);
      setMessage('');
      setPaymentMethod('chapa');
      setBankRef('');
      setBankName('');
    }
  }, [open, user]);

  const close = () => onOpenChange(false);

  const validateStep1 = () => {
    if (!fullName.trim() || !email.trim() || !phone.trim()) {
      toast.error('Please fill name, email and phone'); return false;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Enter a valid email'); return false;
    }
    return true;
  };

  const submit = async () => {
    if (!pkg || !user) return;
    setSubmitting(true);
    try {
      // 1. Create the inquiry record (always — gives Ad Ops visibility).
      const { data: inq, error: inqErr } = await supabase
        .from('package_inquiries' as any)
        .insert({
          package_id: pkg.id,
          user_id: user.id,
          full_name: fullName.trim(),
          company: company.trim() || null,
          phone: phone.trim(),
          email: email.trim().toLowerCase(),
          audience: pkg.audience,
          message: message.trim() || null,
          preferred_payment_method: paymentMethod,
        })
        .select('id')
        .single();
      if (inqErr) throw inqErr;
      const inquiryId = (inq as any).id as string;

      // 2. Manual / talk to sales → no invoice; just notify.
      if (paymentMethod === 'manual') {
        toast.success('Request submitted. Our advertising team will contact you within 1 business day.');
        close();
        navigate('/dashboard/my-package-requests');
        return;
      }

      // 2b. Wallet — debit balance and activate immediately via RPC.
      if (paymentMethod === 'wallet') {
        const { data: subId, error: walletErr } = await supabase.rpc(
          'purchase_package_with_wallet' as any,
          { _package_id: pkg.id },
        );
        if (walletErr) {
          if ((walletErr.message || '').includes('INSUFFICIENT_FUNDS')) {
            toast.error('Insufficient wallet balance. Top up your wallet to continue.');
          } else {
            throw walletErr;
          }
          return;
        }
        toast.success('Package activated! Paid from wallet.');
        close();
        navigate(`/dashboard/wallet/receipt/sub/${subId}`);
        return;
      }


      // 3. Create invoice + checkout via edge function for online + bank.
      const { data: payRes, error: payErr } = await supabase.functions.invoke('payments-create', {
        body: {
          package_id: pkg.id,
          inquiry_id: inquiryId,
          provider: paymentMethod,
          bank_name: paymentMethod === 'bank_transfer' ? (bankName || null) : null,
          payment_reference: paymentMethod === 'bank_transfer' ? (bankRef.trim() || null) : null,
          return_url: `${window.location.origin}/dashboard/my-package-requests`,
        },
      });
      if (payErr) throw payErr;

      const checkoutUrl = (payRes as any)?.checkout_url as string | null;

      if (checkoutUrl) {
        toast.success('Redirecting to payment…');
        window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
        close();
        navigate('/dashboard/my-package-requests');
        return;
      }

      if (paymentMethod === 'bank_transfer') {
        toast.success('Bank transfer recorded. We will activate your package once payment is verified.');
        close();
        navigate('/dashboard/my-package-requests');
        return;
      }

      // Provider not configured yet — fall back to manual confirmation.
      toast.message('Payment provider not yet enabled', {
        description: 'Your request was saved. Our team will reach out with payment instructions.',
      });
      close();
      navigate('/dashboard/my-package-requests');
    } catch (e: any) {
      toast.error(e?.message || 'Could not complete purchase');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep((s) => (s - 1) as 1 | 2)} className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            Purchase {pkg?.tier_label || pkg?.name}
          </DialogTitle>
          <DialogDescription>
            {pkg && (
              <>{pkg.price_currency} {Number(pkg.price_etb).toLocaleString()}{pkg.duration_days ? ` · ${pkg.duration_days} days` : ''}</>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs">
          {(['Your details', 'Payment method', 'Confirm'] as const).map((label, i) => {
            const idx = (i + 1) as 1 | 2 | 3;
            const done = step > idx;
            const active = step === idx;
            return (
              <div key={label} className="flex items-center gap-2 flex-1">
                <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  done ? 'bg-green-600 text-white' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>{done ? <CheckCircle2 className="h-3.5 w-3.5" /> : idx}</div>
                <span className={active ? 'text-foreground font-medium' : 'text-muted-foreground'}>{label}</span>
                {i < 2 && <div className="flex-1 h-px bg-border" />}
              </div>
            );
          })}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Full name *</Label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div>
                <Label>Company / Agency</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+251…" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Notes for our team (optional)</Label>
              <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Anything our advertising team should know…" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <Label className="mb-1 block">Choose how you want to pay</Label>
            <RadioGroup
              value={paymentMethod}
              onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              {PAYMENT_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = paymentMethod === opt.value;
                return (
                  <label
                    key={opt.value}
                    htmlFor={`pm-${opt.value}`}
                    className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                      selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <RadioGroupItem value={opt.value} id={`pm-${opt.value}`} className="mt-0.5" />
                    <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-none">{opt.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">{opt.desc}</p>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>

            {paymentMethod === 'wallet' && pkg && <WalletPayHint amount={Number(pkg.price_etb)} />}

            {paymentMethod === 'bank_transfer' && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
                <p className="text-xs font-medium">Transfer the exact amount to one of our accounts:</p>
                <div className="space-y-2">
                  {BANK_ACCOUNTS.length === 0 ? (
                    <div className="text-xs text-muted-foreground bg-background rounded p-2">
                      Our advertising team will email you the verified bank
                      account details once you submit this request, or contact{' '}
                      <a href="/contact" className="underline">support</a> for the accounts now.
                    </div>
                  ) : BANK_ACCOUNTS.map((b) => (
                    <div key={b.bank} className="text-xs flex items-center justify-between gap-2 bg-background rounded p-2">
                      <div>
                        <p className="font-medium">{b.bank}</p>
                        <p className="text-muted-foreground">{b.name} · <span className="font-mono">{b.account}</span></p>
                      </div>
                      <Button type="button" size="sm" variant="ghost" className="h-7"
                        onClick={() => { navigator.clipboard.writeText(b.account); toast.success('Account copied'); }}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Bank you used</Label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. CBE" />
                  </div>
                  <div>
                    <Label className="text-xs">Transfer reference</Label>
                    <Input value={bankRef} onChange={(e) => setBankRef(e.target.value)} placeholder="FT…" />
                  </div>
                </div>
              </div>
            )}

            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Your subscription activates automatically once payment is confirmed. You can track progress in
                <strong> My Package Requests</strong>.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 3 && pkg && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border p-3 space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Package</span><span className="font-medium">{pkg.tier_label || pkg.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-bold">{pkg.price_currency} {Number(pkg.price_etb).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span className="capitalize">{paymentMethod.replace('_', ' ')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Account</span><span>{email}</span></div>
            </div>
            <p className="text-xs text-muted-foreground">
              Click <strong>Confirm & Pay</strong> to{' '}
              {paymentMethod === 'manual'
                ? 'submit your request — our sales team will contact you.'
                : paymentMethod === 'bank_transfer'
                ? 'register your transfer for verification.'
                : 'open the secure payment page in a new tab.'}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting}>Cancel</Button>
          {step < 3 ? (
            <Button onClick={() => {
              if (step === 1 && !validateStep1()) return;
              setStep((s) => (s + 1) as 2 | 3);
            }}>Continue</Button>
          ) : (
            <Button onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm & Pay
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WalletPayHint({ amount }: { amount: number }) {
  const { balance, isLoading } = useWallet();
  const enough = balance >= amount;
  if (isLoading) return null;
  return (
    <div className={`rounded-lg border p-3 text-xs flex items-start gap-2 ${enough ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-destructive/5 border-destructive/30'}`}>
      <Wallet className={`h-4 w-4 mt-0.5 shrink-0 ${enough ? 'text-emerald-600' : 'text-destructive'}`} />
      <div className="flex-1">
        <p className="font-medium">
          Wallet balance: <span className="tabular-nums">{balance.toLocaleString()}</span> ETB
        </p>
        <p className="text-muted-foreground">
          {enough
            ? `${amount.toLocaleString()} ETB will be deducted on confirm. Package activates instantly.`
            : `Insufficient balance — you need ${(amount - balance).toLocaleString()} ETB more. `}
          {!enough && (
            <a href="/dashboard/wallet" className="text-primary underline">Top up wallet</a>
          )}
        </p>
      </div>
      {!enough && <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />}
    </div>
  );
}
